/**
 * API client for making authenticated requests to Django backend with JWT.
 */

// Use empty string in dev (lets Vite proxy handle it), or explicit URL in production
const API_URL = import.meta.env.VITE_API_URL || ''

// Token management
const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

export interface AuthTokens {
  access: string
  refresh: string
}

export interface LoginCredentials {
  username: string
  password: string
}

export interface InviteRole {
  codename: string
  name: string
}

export interface InviteVerification {
  valid: boolean
  office_name?: string
  available_roles?: InviteRole[]
}

export interface RedeemInvitePayload {
  token: string
  password: string
  email: string
  first_name: string
  last_name: string
  role: string
  cui_attestation: boolean
}

export const tokenManager = {
  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_KEY, tokens.access)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh)
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  },
}

// Authentication API
export const authAPI = {
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const response = await fetch(`${API_URL}/api/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(error.detail || 'Login failed')
    }

    const tokens = await response.json()
    tokenManager.setTokens(tokens)
    return tokens
  },

  async refreshToken(): Promise<string> {
    const refreshToken = tokenManager.getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    const response = await fetch(`${API_URL}/api/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    })

    if (!response.ok) {
      tokenManager.clearTokens()
      throw new Error('Token refresh failed')
    }

    // SIMPLE_JWT['ROTATE_REFRESH_TOKENS'] is on, so the response also carries a
    // new refresh token, and BLACKLIST_AFTER_ROTATION records the one just sent
    // as spent. Storing only the access token leaves the spent refresh token in
    // localStorage, so the next expiry refreshes with a blacklisted credential
    // and logs the user out - once per access-token lifetime, forever.
    const { access, refresh } = await response.json()
    localStorage.setItem(TOKEN_KEY, access)
    if (refresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
    }
    return access
  },

  logout(): void {
    tokenManager.clearTokens()
  },

  async verifyInvite(token: string): Promise<InviteVerification> {
    const response = await fetch(`${API_URL}/api/invites/${encodeURIComponent(token)}/verify/`)
    if (!response.ok) {
      return { valid: false }
    }
    return response.json()
  },

  async redeemInvite(payload: RedeemInvitePayload): Promise<AuthTokens> {
    const response = await fetch(`${API_URL}/api/invites/redeem/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Registration failed' }))
      throw new Error(error.detail || 'Registration failed')
    }

    const tokens = await response.json()
    tokenManager.setTokens(tokens)
    return tokens
  },

  async verifyEmail(uidb64: string, token: string): Promise<{ detail: string }> {
    const response = await fetch(
      `${API_URL}/api/verify-email/${encodeURIComponent(uidb64)}/${encodeURIComponent(token)}/`,
      { method: 'POST' }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Verification failed' }))
      throw new Error(error.detail || 'Verification failed')
    }

    return response.json()
  },

  async requestPasswordReset(email: string): Promise<{ detail: string }> {
    const response = await fetch(`${API_URL}/api/password-reset/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'Could not send the reset email' }))
      throw new Error(error.detail || 'Could not send the reset email')
    }

    return response.json()
  },

  async verifyPasswordResetToken(uidb64: string, token: string): Promise<{ detail: string }> {
    const response = await fetch(
      `${API_URL}/api/password-reset/verify/${encodeURIComponent(uidb64)}/${encodeURIComponent(token)}/`,
      { method: 'POST' }
    )

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ detail: 'This password reset link is invalid or has expired.' }))
      throw new Error(error.detail || 'This password reset link is invalid or has expired.')
    }

    return response.json()
  },

  async confirmPasswordReset(
    uidb64: string,
    token: string,
    password: string
  ): Promise<{ detail: string }> {
    const response = await fetch(
      `${API_URL}/api/password-reset/confirm/${encodeURIComponent(uidb64)}/${encodeURIComponent(token)}/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      }
    )

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Password reset failed' }))
      throw new Error(error.detail || 'Password reset failed')
    }

    return response.json()
  },
}

/**
 * Refresh the access token, coalescing concurrent callers onto one request.
 *
 * A single screen fires many requests at once (opening the vendor drawer fires
 * eight), so an expired token produces a burst of simultaneous 401s. Without
 * coalescing each one would start its own refresh, and ROTATE_REFRESH_TOKENS is
 * enabled server-side, so those refreshes would race to overwrite each other's
 * rotated token.
 */
let refreshInFlight: Promise<string> | null = null

function refreshAccessToken(): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = authAPI.refreshToken().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

// Token endpoints authenticate by refresh token, not by access token — retrying
// them after a 401 would loop.
const REFRESH_EXEMPT_PATHS = ['/api/token/', '/api/token/refresh/']

// The Go transit service signs its own users in, with its own tokens, and
// services/transit.ts refreshes them itself. A 401 there says nothing about the
// Django session, so it must not trigger a Django refresh or the redirect to /.
const OWN_AUTH_PREFIXES = ['/api/transit/']

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return input.url
}

function authorizationHeader(input: RequestInfo | URL, init?: RequestInit): string | null {
  const fromInit = new Headers(init?.headers ?? undefined).get('Authorization')
  if (fromInit) return fromInit
  return input instanceof Request ? input.headers.get('Authorization') : null
}

let interceptorInstalled = false

/**
 * Install a global fetch interceptor that recovers from an expired access token.
 *
 * Most of the app calls `fetch` directly with a hand-attached bearer token
 * rather than going through `apiClient`, so refresh-on-401 has to live at the
 * `fetch` boundary to cover every caller. On a 401 from an authenticated /api/
 * request this refreshes once and replays the original request; if the refresh
 * itself fails the session is genuinely over, so tokens are cleared and the user
 * is sent to the login page (/) rather than left on a page where nothing works.
 */
export function installAuthFetchInterceptor(): void {
  if (interceptorInstalled) return
  interceptorInstalled = true

  const nativeFetch: typeof fetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // A Request body is single-use, so keep a replayable copy before sending.
    const replayable = input instanceof Request ? input.clone() : null
    const response = await nativeFetch(input, init)

    if (response.status !== 401) return response

    const url = requestUrl(input)
    if (!url.includes('/api/')) return response
    if (REFRESH_EXEMPT_PATHS.some((path) => url.includes(path))) return response
    if (OWN_AUTH_PREFIXES.some((prefix) => url.includes(prefix))) return response
    // No bearer token means the request was unauthenticated by design.
    const sentWith = authorizationHeader(input, init)
    if (!sentWith) return response

    // A burst of requests all sent with the same stale token trickles back as
    // 401s over several hundred milliseconds. Once one of them has refreshed,
    // the rest only need replaying — refreshing again per straggler would
    // rotate the refresh token needlessly.
    let token = tokenManager.getAccessToken()
    if (!token || token === sentWith.replace(/^Bearer\s+/i, '')) {
      try {
        token = await refreshAccessToken()
      } catch {
        tokenManager.clearTokens()
        if (window.location.pathname !== '/') {
          window.location.href = '/'
        }
        return response
      }
    }

    // Replay once with the new token. This goes straight to nativeFetch, so a
    // second 401 is returned to the caller rather than retried again.
    const headers = new Headers(replayable ? replayable.headers : init?.headers)
    headers.set('Authorization', `Bearer ${token}`)
    return replayable
      ? nativeFetch(new Request(replayable, { headers }))
      : nativeFetch(input, { ...init, headers })
  }
}

// Make an authenticated API request. Refresh-on-401 is handled by the global
// interceptor above, so there is one refresh path for every caller.
async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = tokenManager.getAccessToken()

  if (!token) {
    throw new Error('Not authenticated')
  }

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
}

// API client with automatic authentication
export const apiClient = {
  async get<T = any>(endpoint: string): Promise<T> {
    const response = await authenticatedFetch(endpoint, {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()
    
    // Handle paginated responses from DRF
    // If the response has a 'results' key, it's paginated - extract the results array
    if (data && typeof data === 'object' && 'results' in data) {
      return data.results as T
    }
    
    return data as T
  },

  async post<T = any>(endpoint: string, data: any): Promise<T> {
    const response = await authenticatedFetch(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || `API error: ${response.status}`)
    }

    return response.json()
  },

  async put<T = any>(endpoint: string, data: any): Promise<T> {
    const response = await authenticatedFetch(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || `API error: ${response.status}`)
    }

    return response.json()
  },

  async patch<T = any>(endpoint: string, data: any): Promise<T> {
    const response = await authenticatedFetch(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || `API error: ${response.status}`)
    }

    return response.json()
  },

  async delete<T = any>(endpoint: string): Promise<T> {
    const response = await authenticatedFetch(endpoint, {
      method: 'DELETE',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || `API error: ${response.status}`)
    }

    // DELETE may return 204 No Content
    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  },
}

// Export default for convenience
export default {
  ...apiClient,
  auth: authAPI,
  tokens: tokenManager,
}
